/**
 * The role matrix. Ported verbatim from the product demonstration.
 *
 * In the demonstration this was enforced in the browser, which is fine for a
 * demonstration and worthless in production. Here it is the single source of
 * truth for `requireRight()` on the server; the web client imports the same
 * table only to decide what to grey out.
 */

export const ROLES = [
  'pcn-manager',
  'practice-manager',
  'receptionist',
  'administrator',
  'care-coordinator',
  'service-lead',
  'comms-approver',
  'org-admin',
] as const;

export type Role = (typeof ROLES)[number];

export const RIGHTS = ['extraction', 'comms', 'translation', 'settings'] as const;
export type Right = (typeof RIGHTS)[number];

export const ROLE_RIGHTS: Record<Role, Record<Right, boolean>> = {
  'pcn-manager': { extraction: true, comms: false, translation: false, settings: false },
  'practice-manager': { extraction: false, comms: true, translation: false, settings: false },
  'receptionist': { extraction: false, comms: false, translation: false, settings: false },
  'administrator': { extraction: false, comms: false, translation: false, settings: false },
  'care-coordinator': { extraction: false, comms: false, translation: false, settings: false },
  'service-lead': { extraction: true, comms: false, translation: false, settings: false },
  'comms-approver': { extraction: false, comms: true, translation: true, settings: false },
  'org-admin': { extraction: true, comms: true, translation: true, settings: true },
};

export const ROLE_LABELS: Record<Role, string> = {
  'pcn-manager': 'PCN manager',
  'practice-manager': 'Practice manager',
  'receptionist': 'Receptionist',
  'administrator': 'Administrator',
  'care-coordinator': 'Care coordinator',
  'service-lead': 'Service lead',
  'comms-approver': 'Communications approver',
  'org-admin': 'Organisation administrator',
};

export function can(role: Role, right: Right): boolean {
  return ROLE_RIGHTS[role]?.[right] === true;
}

/**
 * Approving a communication or a translation is a named act with a named
 * person against it. Only these two roles may do it.
 */
export function canApproveComms(role: Role): boolean {
  return role === 'comms-approver' || role === 'org-admin';
}

export function canApproveTranslation(role: Role): boolean {
  return can(role, 'translation');
}
